import { computed, ref } from 'vue';

import { ProductCategory } from '@/clients/unifiedApi';
import { PaginatedResponse } from '@/models/api';
import { PromoCode } from '@/models/company';
import {
  FacilityManagement,
  InventoryProduct,
  InventoryProductAssigned,
  InventoryProductWithAllocations,
  ProductBatch,
  ProductInventory,
  StrainClassification,
} from '@/models/inventory';
import { ListingState, Product, ProductLine } from '@/models/product';
import { CustomMenu, CustomMenuDetail } from '@/services/api/models/customMenus';

export const setupInventoryState = () => {
  const associatedBatches = ref<ProductBatch[]>([]);
  const categories = ref<ProductCategory[]>([]);
  const productCategories = ref<ProductCategory[]>([]);

  const customMenu = ref<CustomMenuDetail>();
  const customMenuList = ref<CustomMenu[]>([]);
  const isLoadingProducts = ref<boolean>(false);
  const isLoadingProductInventory = ref<boolean>(false);
  const productLines = ref<ProductLine[]>([]);
  const products = ref<(Product | ProductInventory)[]>([]);
  const promoCodes = ref<PromoCode[]>([]);
  const isLoadingCustomMenu = ref<boolean>(false);
  const isLoadingCustomMenuList = ref<boolean>(false);
  const listingStates = ref<ListingState[]>([]);
  const isLoadingListings = ref<boolean>(false);
  const isLoadingProductsAssigned = ref<boolean>(false);
  const paginatedListings = ref<PaginatedResponse<InventoryProductWithAllocations>>({
    count: 0,
    results: [],
  });
  const paginatedProductsAssigned = ref<PaginatedResponse<InventoryProductAssigned>>({
    count: 0,
    results: [],
  });
  const isLoadingAssociatedBatches = ref<boolean>(false);
  const paginatedProductInventory = ref<PaginatedResponse<ProductInventory>>({
    count: 0,
    results: [],
  });
  const paginatedProducts = ref<PaginatedResponse<InventoryProduct>>({
    count: 0,
    results: [],
  });
  const facilityManagement = ref<FacilityManagement>({
    isLoadingFacilities: false,
    paginatedFacilities: {
      count: 0,
      results: [],
    },
    selectedGoogleAddress: {
      address: '',
      city: '',
      country: '',
      state: '',
      unitNumber: '',
      zipcode: '',
      lat: 0,
      long: 0,
    },
  });
  const strainClassifications = ref<StrainClassification[]>([]);

  const getCategory = computed(
    () => (categoryId: ProductCategory['id']) => categories.value.find(({ id }) => id === categoryId),
  );

  const getCustomMenu = computed(() => {
    return (scheduleId?: CustomMenu['id']) => {
      if (!scheduleId) {
        return;
      }

      return customMenuList.value.find((cm) => cm.id === scheduleId);
    };
  });

  const getProduct = computed(() => (id: Product['id']) => products.value.find((c) => c.id === id));

  const getProductLine = computed(
    () => (productLineId: ProductLine['id']) => productLines.value.find(({ id }) => id === productLineId),
  );

  return {
    associatedBatches,
    categories,
    productCategories,

    customMenu,
    customMenuList,
    isLoadingProducts,
    isLoadingProductInventory,
    productLines,
    products,
    promoCodes,
    isLoadingCustomMenu,
    isLoadingCustomMenuList,
    listingStates,
    isLoadingListings,
    isLoadingProductsAssigned,
    paginatedListings,
    paginatedProductsAssigned,
    isLoadingAssociatedBatches,
    paginatedProductInventory,
    paginatedProducts,
    facilityManagement,
    strainClassifications,

    getCategory,
    getCustomMenu,
    getProduct,
    getProductLine,
  };
};

export type InventoryState = ReturnType<typeof setupInventoryState>;
